import { useSyncExternalStore } from "react";
import { api, TOKEN_KEY } from "@/lib/api";
import { probe, isOnline } from "@/lib/connectivity";
import { device, ldb } from "@/lib/localdb";
import { takeNumber } from "@/lib/receiptBlocks";
import { minorToStr, toMinor } from "@/lib/money";

// Outbox of sales saved on this device. Uploaded with backoff to the idempotent /api/sales/sync.
let state = { items: [], pending: 0, failed: 0, syncing: 0 };
let businessId = null;
const subs = new Set();
const doneSubs = new Set();
const emit = () => subs.forEach((f) => f());

export async function reloadOutbox() {
  const items = businessId ? await ldb.outbox.where("business_id").equals(businessId).toArray() : [];
  items.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  state = { ...state, items, pending: items.filter((i) => i.status === "pending").length, failed: items.filter((i) => i.status === "failed").length };
  emit();
}

export function setOutboxBusiness(id) {
  if (id === businessId) return;
  businessId = id;
  reloadOutbox();
}

export const useOutbox = () => useSyncExternalStore((f) => (subs.add(f), () => subs.delete(f)), () => state);
export const onSynced = (f) => (doneSubs.add(f), () => doneSubs.delete(f));

export function buildLocalSale({ cart, payment, session, numbers, clientSaleId }) {
  const biz = session.business, br = session.branch, dp = biz.decimals, t = cart.totals;
  const tendered = payment.method === "cash" ? toMinor(payment.tendered, dp) : t.total;
  return {
    client_sale_id: clientSaleId, device_id: device().id, business_id: biz.id, branch_id: br.id, user_id: session.user.id, ...numbers,
    created_at: new Date().toISOString(), currency: biz.currency, decimals: dp, tax_label: biz.tax_label, tax_inclusive: biz.tax_inclusive,
    lines: cart.rows.map((r) => ({
      product_id: r.product_id, name: r.product.name, barcode: r.product.barcode || null, unit_minor: toMinor(r.product.price, dp), qty: r.qty,
      rate_bp: r.calc.rate_bp, discount_input: r.discount || null, gross: r.calc.gross, line_discount: r.calc.line_discount, net: r.calc.net,
      cart_alloc: r.calc.cart_alloc, taxable: r.calc.taxable, tax: r.calc.tax, allocations: r.allocations,
    })),
    cart_discount_input: cart.cartDiscount || null,
    totals: {
      gross_total: t.gross_total, line_discount_total: t.line_discount_total, subtotal: t.subtotal, cart_discount: t.cart_discount,
      tax_total: t.tax_total, tax_breakdown: t.tax_breakdown, total: t.total, total_str: minorToStr(t.total, dp),
    },
    payment: { method: payment.method, tendered, change: tendered - t.total, amount: t.total },
    business_snapshot: {
      name: biz.name, business_type: biz.business_type, country: biz.country, tax_number: biz.tax_number, receipt_header: biz.receipt_header,
      receipt_footer: biz.receipt_footer, branch_name: br.name, branch_address: br.address, branch_phone: br.phone,
    },
    status: "completed",
  };
}

export async function saveOfflineSale(args) {
  const sale = buildLocalSale({ ...args, numbers: args.numbers || (await takeNumber(args.session.branch)) });
  await ldb.outbox.put({ client_sale_id: sale.client_sale_id, business_id: sale.business_id, branch_id: sale.branch_id, created_at: sale.created_at, status: "pending", attempts: 0, sale });
  await reloadOutbox();
  if (isOnline()) setTimeout(() => syncNow(), 300);
  return sale;
}

let running = null, retry = null, attempts = 0;
export function syncNow(tokenOverride) {
  if (!running) running = doSync(tokenOverride).finally(() => (running = null));
  return running;
}

async function doSync(tokenOverride) {
  const token = tokenOverride || localStorage.getItem(TOKEN_KEY);
  if (!token || !businessId) return;
  const items = (await ldb.outbox.where("business_id").equals(businessId).toArray()).filter((i) => i.status === "pending").sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
  if (!items.length) return;
  clearTimeout(retry);
  state = { ...state, syncing: items.length };
  emit();
  let synced = 0;
  try {
    for (let i = 0; i < items.length; i += 25) {
      const chunk = items.slice(i, i + 25);
      const { data } = await api.post("/sales/sync", { sales: chunk.map((x) => (x.sale && typeof x.sale === "object" && !Array.isArray(x.sale) ? { ...x.sale, client_sale_id: x.client_sale_id } : { client_sale_id: x.client_sale_id })) }, { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 });
      for (const r of data.results) {
        if (r.status === "rejected") await ldb.outbox.update(r.client_sale_id, { status: "failed", error: r.error });
        else {
          await ldb.outbox.delete(r.client_sale_id);
          synced += 1;
        }
      }
      state = { ...state, syncing: Math.max(0, items.length - i - chunk.length) };
      await reloadOutbox();
    }
    attempts = 0;
    if (synced && !state.pending) doneSubs.forEach((f) => f(synced));
  } catch (e) {
    if (!e?.response) probe();
    if (e?.response?.status !== 401) {
      attempts += 1;
      retry = setTimeout(() => isOnline() && syncNow(), Math.min(60, 2 ** attempts) * 1000);
    }
  } finally {
    state = { ...state, syncing: 0 };
    await reloadOutbox();
  }
}
